'use client';

import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import capitalize from 'lodash.capitalize';
import lowerCase from 'lodash.lowercase';

import type { FieldDescriptor } from '../Entities/FinCalcFields.entity';
import { calculationAtom } from './Calculation.atom';
import { finCalcDataFieldValueAtom } from './FinCalcDataFieldValue.atom';
import { finCalcMessagesAtom } from './FinCalcMessages.atom';

export const finCalcDataAtom = atom<Record<string, any>>({
  isLoading: true,
});

export const hasFinCalcDataLoadedAtom = atom(get => !get(finCalcDataAtom).isLoading);

export const finCalcDataFieldAtom = atomFamily(
  ({
    id,
    type,
    label,
    videoLink,
    width,
    isActive,
    isValid,
    errorMessage,
    inputType,
    options,
    minDate,
    maxDate,
    leftIcon,
    rightIcon,
    dataTestId,
    isDisabled,
    radioOptions,
    defaultValue,
    checkboxOptions,
    mask,
    min,
    max,
    rangeLabel,
    currencySymbol,
    hasInfo = false,
    readOnly = false,
    step,
  }: FieldDescriptor) =>
    atom(get => {
      const calculation = get(calculationAtom)!;
      const finCalcData = get(finCalcDataAtom)!;
      const identifier = id;
      const value = get(finCalcDataFieldValueAtom(identifier));
      const messages = get(finCalcMessagesAtom);
      const productId = calculation.selectedProduct as unknown as any;
      const defaultProduct = calculation.products?.[productId];

      // To ensure all available products are visible, HP is used for the date selector.
      const hpProduct = calculation.products['HP' as unknown as any];
      const deposit = hpProduct ? hpProduct.inputs.deposit : defaultProduct.inputs.deposit;
      const months = hpProduct ? hpProduct.inputs.months : defaultProduct.inputs.months;
      const inputs = { ...(defaultProduct?.inputs || {}), deposit, months };
      const product = { ...defaultProduct, inputs };

      const baseProps = { value, id, finCalcData, calculation, productId, product, messages };

      const labelStr = label?.(baseProps) || capitalize(lowerCase(id));
      const widthVal = width?.(baseProps);
      const videoLinkVal = videoLink?.(baseProps);
      const isActiveVal = isActive?.(baseProps);
      const isValidVal = isValid?.(baseProps) ?? true;
      const errorMessageVal = errorMessage?.(baseProps);
      const inputTypeVal = inputType?.(baseProps);
      const optionsVal = options?.(baseProps);
      const minDateVal = minDate?.(baseProps);
      const maxDateVal = maxDate?.(baseProps);
      const isDisabledVal = isDisabled?.(baseProps);
      const radioOptionsVal = radioOptions?.(baseProps);
      const checkboxOptionsVal = checkboxOptions?.(baseProps);
      const defaultValueVal = defaultValue?.(baseProps);
      const maskVal = mask?.(baseProps);
      const minimum = min?.(baseProps);
      const maximum = max?.(baseProps);
      const isLabel = rangeLabel?.(baseProps);
      const isStep = step?.(baseProps);

      return {
        id,
        identifier,
        dataTestId,
        width: widthVal,
        isActive: isActiveVal,
        defaultValue: defaultValueVal,
        label: labelStr,
        videoLink: videoLinkVal,
        isValid: isValidVal,
        options: optionsVal,
        errorMessage: errorMessageVal,
        type,
        minDate: minDateVal,
        maxDate: maxDateVal,
        leftIcon,
        rightIcon,
        inputType: inputTypeVal,
        isDisabled: isDisabledVal,
        checkboxOptions: checkboxOptionsVal,
        radioOptions: radioOptionsVal,
        mask: maskVal,
        min: minimum,
        max: maximum,
        rangeLabel: isLabel,
        step: isStep,
        currencySymbol,
        hasInfo,
        readOnly,
      };
    }),
  (prev, next) => next.id === prev.id,
);
